import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader, Loading, Empty, fmtDate } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Bell, CheckCheck, Users, Repeat, Clock, Calendar, IndianRupee, AlertTriangle, UserX } from "lucide-react";
import { toast } from "sonner";

const ICONS = { new_lead: Users, repeat_enquiry: Repeat, followup_due: Clock, appointment: Calendar, fee_due: IndianRupee, unassigned: UserX, integration_failure: AlertTriangle };

export default function Notifications() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({ queryKey: ["notifications"], queryFn: async () => (await api.get("/notifications")).data, refetchInterval: 15000 });

  const markRead = useMutation({ mutationFn: async (id) => (await api.post(`/notifications/${id}/read`)).data, onSuccess: () => qc.invalidateQueries() });
  const markAll = useMutation({ mutationFn: async () => (await api.post("/notifications/read-all")).data, onSuccess: () => { toast.success("All marked read"); qc.invalidateQueries(); } });

  const openNotif = (n) => { if (!n.read) markRead.mutate(n.id); if (n.link) navigate(n.link); };

  return (
    <div>
      <PageHeader title="Notifications" subtitle="Persistent alerts. Updates poll every 15s (server-side scheduler runs even when this tab is closed)."
        actions={<Button variant="outline" className="gap-2" onClick={() => markAll.mutate()} data-testid="mark-all-read"><CheckCheck className="w-4 h-4" /> Mark all read</Button>} testid="notifications-header" />

      {isLoading ? <Loading /> : !data?.items?.length ? <Empty title="No notifications" subtitle="You're all caught up." /> : (
        <div className="space-y-2">
          {data.items.map((n) => {
            const Icon = ICONS[n.type] || Bell;
            return (
              <div key={n.id} onClick={() => openNotif(n)} data-testid={`notif-${n.id}`}
                className={`p-4 rounded-xl border flex items-start gap-3 cursor-pointer hover:shadow-md transition-shadow ${n.read ? "bg-white dark:bg-slate-800" : "bg-blue-50 dark:bg-blue-950/30 border-blue-200 dark:border-blue-900"}`}>
                <div className={`w-9 h-9 rounded-lg grid place-items-center shrink-0 ${n.read ? "bg-slate-100 dark:bg-slate-700 text-slate-500" : "bg-brand-blue text-white"}`}><Icon className="w-4 h-4" /></div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm">{n.title}</div>
                  {n.body && <div className="text-sm text-slate-500">{n.body}</div>}
                  <div className="text-[11px] text-slate-400 mt-0.5">{fmtDate(n.created_at)}</div>
                </div>
                {!n.read && <span className="w-2 h-2 rounded-full bg-orange-500 mt-2 shrink-0" />}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
